import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Shield, Globe, Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'ar', name: 'العربية' },
  { code: 'zh', name: '繁體中文' }
];

const ForgotPassword = () => {
  const { t, language, changeLanguage } = useLanguage();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      
      toast.success(language === 'es' 
        ? 'Instrucciones enviadas por correo' 
        : 'Reset instructions sent to your email');
    } catch (error) {
      toast.error(language === 'es' 
        ? 'Error al procesar solicitud' 
        : 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Shield className="h-8 w-8 text-primary" />
          <span className="font-bold text-xl tracking-tight">PoliBot</span>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language.toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {languages.map(lang => (
              <DropdownMenuItem 
                key={lang.code}
                onClick={() => changeLanguage(lang.code)} 
                className={language === lang.code ? 'bg-muted' : ''}
              >
                {lang.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md animate-scale-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">
              {language === 'es' ? 'Restablecer Contraseña' : 'Reset Password'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Ingresa tu correo electrónico para recibir instrucciones'
                : 'Enter your email to receive reset instructions'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{language === 'es' ? 'Correo Electrónico' : 'Email'}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@organization.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {language === 'es' ? 'Enviando...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Enviar Instrucciones' : 'Send Instructions'}
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col items-center text-center py-4">
                  <CheckCircle className="h-12 w-12 text-success mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    {language === 'es' ? 'Correo Enviado' : 'Email Sent'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'es' 
                      ? 'Si existe una cuenta con ese correo, recibirás un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada.'
                      : 'If an account exists with that email, you will receive a password reset link. Please check your inbox.'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-3">
                    {language === 'es' 
                      ? 'El enlace expirará en 1 hora.'
                      : 'The link will expire in 1 hour.'}
                  </p>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                >
                  {language === 'es' ? 'Enviar a Otro Correo' : 'Try Different Email'}
                </Button>
              </div>
            )}
            
            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                data-testid="back-to-login"
              >
                <ArrowLeft className="h-4 w-4" />
                {language === 'es' ? 'Volver a Iniciar Sesión' : 'Back to Login'}
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ForgotPassword;
